/**
 * A header a flow sends on an outbound request, whose value is held in Secret Manager rather than
 * in the flow.
 *
 * The value is write-only: it is carried by the create and rotate bodies below and by nothing else,
 * and flow-api keeps a reference to it rather than the secret. Everything a header is described by
 * — its name, header name, domain, organization and the reference itself — is read back freely.
 */
import { z } from 'zod';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
/** Query parameters `GET /secure-headers` accepts. */
export const FlowSecureHeaderListQuerySchema = z.object({
    organizationId: z
        .union([z.array(z.uuid()), z.uuid()])
        .describe('Return only headers of this organization, or of any of these organizations.')
        .optional(),
    domain: z.string().min(4).describe('Return only headers sent to this domain.').optional(),
    ids: z.array(z.uuid()).describe('Return only these headers.').optional(),
    limit: FlowApiPageLimitSchema.describe('How many headers to return.').optional(),
    offset: FlowApiPageOffsetSchema.describe('How many matches to skip, for paging.').optional(),
    orderBy: z.string().describe('Column to order by, such as name.').optional(),
    orderDirection: z.enum(['asc', 'desc']).describe('Direction to order by.').optional(),
});
/** Path parameters the endpoints addressing one secure header accept. */
export const FlowSecureHeaderIdParamsSchema = z.object({
    id: z.uuid().describe('The secure header.'),
});
/**
 * Body `POST /secure-headers` accepts. `headerValue` is the secret: flow-api stores it in Secret
 * Manager and keeps only a reference, so this is one of the two requests that ever carries it.
 */
export const FlowSecureHeaderCreateBodySchema = z.object({
    name: z.string().min(1).describe('Name the header is listed under.'),
    organizationId: z.uuid().describe('Organization the header belongs to.'),
    headerName: z.string().min(1).describe('The header sent on the request, such as Authorization.'),
    headerValue: z.string().min(1).describe('The secret value to store.'),
    domain: z.string().min(4).describe('Domain the header may be sent to.'),
});
/**
 * Body `PATCH /secure-headers/{id}` accepts.
 *
 * Neither the organization nor the domain is among them: both are part of what the stored secret is
 * scoped to, so moving a header means creating another one.
 */
export const FlowSecureHeaderUpdateBodySchema = z.object({
    name: z.string().min(1).optional(),
    headerName: z.string().min(1).optional(),
});
/** Body `PATCH /secure-headers/{id}/secure-header-value` accepts, which rotates the secret. */
export const FlowSecureHeaderValueUpdateBodySchema = z.object({
    secureHeaderValue: z.string().min(1).describe('The replacement secret value.'),
});
//# sourceMappingURL=secureHeader.js.map